/* eslint-disable react-refresh/only-export-components, max-lines */
import {
  DimeButton,
  DimeButtonGroup,
  DimeButtonGroupSeparator,
  DimeButtonGroupText,
  DimeDropdownMenu,
  DimeDropdownMenuContent,
  DimeDropdownMenuItem,
  DimeDropdownMenuSeparator,
  DimeDropdownMenuTrigger,
  DimeInput,
  DimeInputGroup,
  DimeInputGroupAddon,
  DimeInputGroupInput,
  DimeLabel,
  DimeSelect,
  DimeSelectOption,
  DimePopover,
  DimePopoverContent,
  DimePopoverDescription,
  DimePopoverHeader,
  DimePopoverTitle,
  DimePopoverTrigger,
  DimeSeparator,
} from '@nudool/design-system';
import { createFileRoute } from '@tanstack/react-router';
import { ChevronDown, Copy, MoreHorizontal, Plus, Settings, Trash2 } from 'lucide-react';
import * as React from 'react';

export const Route = createFileRoute('/design-system/button-group')({
  component: ButtonGroupDocs,
});

function DocsSection({ title, children }: Readonly<{ title: string; children: React.ReactNode }>) {
  return (
    <section className="mb-10">
      <h2 className="mb-1 text-xl font-semibold">{title}</h2>
      <DimeSeparator className="mb-4" />
      {children}
    </section>
  );
}

function Preview({ children }: Readonly<{ children: React.ReactNode }>) {
  return (
    <div className="flex flex-wrap items-center gap-4 rounded-lg border border-border bg-background p-6">
      {children}
    </div>
  );
}

function ButtonGroupDocs() {
  return (
    <div>
      <h1 className="mb-1 text-3xl font-bold">Button Group</h1>
      <p className="mb-8 text-muted-foreground">
        A container that groups related buttons together with consistent styling. Use{' '}
        <code className="font-mono text-sm">DimeButtonGroup</code> for actions; use a toggle group
        when buttons should toggle state.
      </p>

      <DocsSection title="Basic">
        <Preview>
          <DimeButtonGroup aria-label="Button group">
            <DimeButton variant="outline">Button 1</DimeButton>
            <DimeButton variant="outline">Button 2</DimeButton>
          </DimeButtonGroup>
        </Preview>
        <p className="mt-3 text-sm text-muted-foreground">
          Use <code className="font-mono text-xs">aria-label</code> or{' '}
          <code className="font-mono text-xs">aria-labelledby</code> to label the group for
          assistive technology.
        </p>
      </DocsSection>

      <DocsSection title="Orientation">
        <Preview>
          <DimeButtonGroup aria-label="Horizontal button group">
            <DimeButton variant="outline">One</DimeButton>
            <DimeButton variant="outline">Two</DimeButton>
            <DimeButton variant="outline">Three</DimeButton>
          </DimeButtonGroup>
          <DimeButtonGroup aria-label="Vertical button group" orientation="vertical">
            <DimeButton variant="outline">One</DimeButton>
            <DimeButton variant="outline">Two</DimeButton>
            <DimeButton variant="outline">Three</DimeButton>
          </DimeButtonGroup>
        </Preview>
      </DocsSection>

      <DocsSection title="Size">
        <Preview>
          <DimeButtonGroup aria-label="Small buttons">
            <DimeButton size="sm" variant="outline">
              Small
            </DimeButton>
            <DimeButton size="sm" variant="outline">
              Group
            </DimeButton>
          </DimeButtonGroup>
          <DimeButtonGroup aria-label="Default buttons">
            <DimeButton variant="outline">Default</DimeButton>
            <DimeButton variant="outline">Group</DimeButton>
          </DimeButtonGroup>
          <DimeButtonGroup aria-label="Large buttons">
            <DimeButton size="lg" variant="outline">
              Large
            </DimeButton>
            <DimeButton size="lg" variant="outline">
              Group
            </DimeButton>
          </DimeButtonGroup>
        </Preview>
      </DocsSection>

      <DocsSection title="Nested">
        <Preview>
          <DimeButtonGroup aria-label="Nested button groups">
            <DimeButtonGroup>
              <DimeButton variant="outline">Copy</DimeButton>
              <DimeButton variant="outline">Paste</DimeButton>
            </DimeButtonGroup>
            <DimeButtonGroup>
              <DimeButton variant="outline">Cut</DimeButton>
            </DimeButtonGroup>
          </DimeButtonGroup>
        </Preview>
      </DocsSection>

      <DocsSection title="Separator">
        <Preview>
          <div className="flex flex-col gap-4">
            <DimeButtonGroup aria-label="Default variant with separator">
              <DimeButton>Save</DimeButton>
              <DimeButtonGroupSeparator />
              <DimeButton>Publish</DimeButton>
            </DimeButtonGroup>
            <DimeButtonGroup aria-label="Outline variant without separator">
              <DimeButton variant="outline">Save</DimeButton>
              <DimeButton variant="outline">Publish</DimeButton>
            </DimeButtonGroup>
          </div>
        </Preview>
        <p className="mt-3 text-sm text-muted-foreground">
          Outline buttons already have borders; other variants benefit from{' '}
          <code className="font-mono text-xs">DimeButtonGroupSeparator</code> for visual hierarchy.
        </p>
      </DocsSection>

      <DocsSection title="Split">
        <Preview>
          <DimeButtonGroup aria-label="Split button group">
            <DimeButton>Create</DimeButton>
            <DimeButtonGroupSeparator />
            <DimeButton size="icon" aria-label="More create options">
              <ChevronDown />
            </DimeButton>
          </DimeButtonGroup>
        </Preview>
      </DocsSection>

      <DocsSection title="Text">
        <Preview>
          <DimeButtonGroup aria-label="Button group with text">
            <DimeButtonGroupText>https://</DimeButtonGroupText>
            <DimeButton variant="outline">Visit</DimeButton>
          </DimeButtonGroup>
        </Preview>
      </DocsSection>

      <DocsSection title="Text with label">
        <Preview>
          <DimeButtonGroup aria-label="Labeled input group">
            <DimeButtonGroupText asChild>
              <DimeLabel htmlFor="button-group-name">Name</DimeLabel>
            </DimeButtonGroupText>
            <DimeInput
              showClear={false}
              id="button-group-name"
              placeholder="Type something here..."
            />
          </DimeButtonGroup>
        </Preview>
      </DocsSection>

      <DocsSection title="Input">
        <Preview>
          <DimeButtonGroup aria-label="Input with buttons" className="w-full max-w-md">
            <DimeButton variant="outline" size="icon" aria-label="Add">
              <Plus />
            </DimeButton>
            <DimeInput showClear={false} placeholder="Search..." />
            <DimeButton variant="outline">Go</DimeButton>
          </DimeButtonGroup>
        </Preview>
      </DocsSection>

      <DocsSection title="Input group">
        <Preview>
          <DimeButtonGroup aria-label="Input group with button" className="w-full max-w-md">
            <DimeInputGroup>
              <DimeInputGroupAddon align="inline-start">https://</DimeInputGroupAddon>
              <DimeInputGroupInput placeholder="example.com" />
            </DimeInputGroup>
            <DimeButton variant="outline">Visit</DimeButton>
          </DimeButtonGroup>
        </Preview>
      </DocsSection>

      <DocsSection title="Dropdown menu">
        <Preview>
          <DimeButtonGroup aria-label="Split button with dropdown">
            <DimeButton>Update</DimeButton>
            <DimeButtonGroupSeparator />
            <DimeDropdownMenu>
              <DimeDropdownMenuTrigger asChild>
                <DimeButton size="icon" aria-label="Update options">
                  <ChevronDown />
                </DimeButton>
              </DimeDropdownMenuTrigger>
              <DimeDropdownMenuContent align="end">
                <DimeDropdownMenuItem>
                  <Settings />
                  Settings
                </DimeDropdownMenuItem>
                <DimeDropdownMenuItem>
                  <Copy />
                  Copy link
                </DimeDropdownMenuItem>
                <DimeDropdownMenuSeparator />
                <DimeDropdownMenuItem variant="destructive">
                  <Trash2 />
                  Delete
                </DimeDropdownMenuItem>
              </DimeDropdownMenuContent>
            </DimeDropdownMenu>
          </DimeButtonGroup>
        </Preview>
      </DocsSection>

      <DocsSection title="Select">
        <Preview>
          <DimeButtonGroup aria-label="Amount with currency" className="w-full max-w-sm">
            <DimeButtonGroupText>Amount</DimeButtonGroupText>
            <DimeSelect defaultValue="usd" aria-label="Currency">
              <DimeSelectOption value="usd">$</DimeSelectOption>
              <DimeSelectOption value="eur">€</DimeSelectOption>
              <DimeSelectOption value="gbp">£</DimeSelectOption>
            </DimeSelect>
            <DimeInput type="number" placeholder="0.00" />
          </DimeButtonGroup>
        </Preview>
        <p className="mt-3 text-sm text-muted-foreground">
          The <code className="font-mono text-xs">DimeSelect</code> primitive sits inside a group as
          a compact inline selector alongside inputs or buttons. To pick values in app forms and
          filters, use <code className="font-mono text-xs">DimeAutocomplete</code>.
        </p>
      </DocsSection>

      <DocsSection title="Popover">
        <Preview>
          <DimeButtonGroup aria-label="Button group with popover">
            <DimeButton variant="outline">Action</DimeButton>
            <DimePopover>
              <DimePopoverTrigger asChild>
                <DimeButton variant="outline" size="icon" aria-label="Open popover">
                  <MoreHorizontal />
                </DimeButton>
              </DimePopoverTrigger>
              <DimePopoverContent align="end">
                <DimePopoverHeader>
                  <DimePopoverTitle>Actions</DimePopoverTitle>
                  <DimePopoverDescription>Choose an action for this item.</DimePopoverDescription>
                </DimePopoverHeader>
                <div className="flex flex-col gap-1">
                  <DimeButton variant="ghost" className="justify-start">
                    Edit
                  </DimeButton>
                  <DimeButton variant="ghost" className="justify-start">
                    Duplicate
                  </DimeButton>
                </div>
              </DimePopoverContent>
            </DimePopover>
          </DimeButtonGroup>
        </Preview>
      </DocsSection>
    </div>
  );
}
